import { Modal, Radio, Space } from 'antd'
import StyledLoader from 'components/Elements/StyledLoader'
import { DataContext } from 'contexts/DataContextContainer'
import { mySQLDate } from 'lib/date'
import { useContext, useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import axiosClient from 'services/axios-client'
import { ROLES } from 'services/constants'

// Component that allows configuring user roles for prompts, as well as mailing.
// Has a bunch of different modes that cause it to display different modals.
// Will display if promptRolesModalConfig has a userID set

// TODO: Fix crazy modalType setup.

/** 1: author or reader, 2: purchaser or editor & is_sent=0, 3: purchaser or editor & is_sent=1*/
export default function PromptRolesModal({ config, onUpdate }) {
  const { question, type, show } = config
  const { t } = useTranslation()
  const { setPromptRolesModalConfig } = useContext(DataContext)

  const { lang } = useContext(DataContext)
  const { bookId } = useParams()
  const [questionUsers, setQuestionUsers] = useState([])
  const [isGettingUsers, setIsGettingUsers] = useState(true)
  const [userRoleOptions, setUserRoleOptions] = useState({})
  const [userCurrentRoles, setUserCurrentRoles] = useState({})

  const rolesToDisplay = JSON.parse(JSON.stringify(ROLES))
  delete rolesToDisplay['purchaser']
  delete rolesToDisplay['guest']
  delete rolesToDisplay['none']

  useEffect(() => {
    axiosClient
      .get(`/questions/${question.book_question_join_id}/users`)
      .then(({ ok, data }) => {
        if (ok) {
          setQuestionUsers(data)
          let _userRoleOptions = {}
          data.forEach((u) => {
            _userRoleOptions[u.id] = Object.values(ROLES).find(
              (r) => r.value === u.role_type_id,
            ).id
          })

          setUserRoleOptions(JSON.parse(JSON.stringify(_userRoleOptions)))
          setUserCurrentRoles(JSON.parse(JSON.stringify(_userRoleOptions)))
          setIsGettingUsers(false)
        }
      })
  }, [question.book_question_join_id])

  const rolesToSet = ['author', 'reader', 'none']
  const actionButtonTitles = {
    1: 'close',
    2: 'save',
    3: 'save',
    4: t('tt_email_prompt'),
  }

  const handleCloseModal = () => {
    setPromptRolesModalConfig({})
  }

  const sendPrompt = async () => {
    let userRoles = []
    for (let userId in userRoleOptions) {
      if (
        userRoleOptions[userId] !== 'purchaser' &&
        userRoleOptions[userId] !== 'editor'
      ) {
        userRoles.push({
          user_id: userId,
          role: userRoleOptions[userId],
        })
      }
    }

    const { ok } = await axiosClient.put(`/questions/sendQuestion`, {
      book_id: bookId,
      question_id: question.id,
      book_question_join_id: question['book_question_join_id'],
      sent_datetime: mySQLDate(new Date()),
      roles: userRoles,
    })
    if (ok) {
      onUpdate({ category: 'update_book', detail: {} })
      handleCloseModal()
    }
  }

  const updateRole = async () => {
    let userRoles = []
    for (let userId in userRoleOptions) {
      if (
        userRoleOptions[userId] !== 'purchaser' &&
        userRoleOptions[userId] !== 'editor'
      ) {
        userRoles.push({
          user_id: userId,
          role: userRoleOptions[userId],
        })
      }
    }

    let promises = []
    for (let userRole of userRoles) {
      promises.push(
        axiosClient.post(`/role/createUpdate`, {
          role_type_id: `${ROLES[userRole['role']].value}`,
          role_link_type_id: '8',
          role_link_to_id: question['book_question_join_id'],
          user_id: userRole['user_id'],
          book_id: bookId,
        }),
      )
    }

    await Promise.all(promises)
    onUpdate({ category: 'update_book', detail: {} })
    handleCloseModal()
  }

  const onChangeUserRoleOption = (user_id, roleId) => {
    let _userRoleOptions = userRoleOptions
    _userRoleOptions[user_id] = roleId
    setUserRoleOptions(JSON.parse(JSON.stringify(_userRoleOptions)))
  }

  const actions = {
    1: handleCloseModal,
    2: updateRole,
    3: updateRole,
  }

  return (
    <>
      <Modal
        title={t('edit_story_prompt_roles')}
        open={show && type !== 4}
        maskClosable={false}
        footer={false}
        centered={true}
        width={600}
        onCancel={handleCloseModal}
        wrapClassName="modal-roles-wrapper"
        getContainer={() => document.getElementById('story-section')}
      >
        <h3 className="mb-20">
          <i>{question.dsply_text}</i>
        </h3>

        {type === 1 &&
          Object.values(rolesToDisplay).map((role) => (
            <div
              key={role.id}
              className="modal-roles modal-custom-body p20 mb-20"
            >
              <div className="role-section">
                <h6 className="header">{t(`${role.id}s`)}</h6>
                <div className="users">
                  {isGettingUsers ? (
                    <span>{t('loading')}...</span>
                  ) : (
                    <>
                      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {questionUsers.filter(
                          (qu) => qu.role_type_id === role.value,
                        ).length > 0 ? (
                          questionUsers
                            .filter((qu) => qu.role_type_id === role.value)
                            .map((qu, index) => (
                              <h6 key={qu.id}>
                                {index === 0
                                  ? `${qu.first_name} ${qu.last_name}`
                                  : `, ${qu.first_name} ${qu.last_name}`}
                              </h6>
                            ))
                        ) : (
                          <span>
                            {t('no')} {t(`${role.id}s`)}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}

        {type !== 1 && (
          <>
            <div className="modal-roles modal-custom-body p20">
              <div className="role-section">
                <h6 className="header">{t('editors')}</h6>
                <div className="users">
                  {isGettingUsers ? (
                    <StyledLoader isglobal={false} />
                  ) : (
                    <>
                      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {questionUsers.filter(
                          (qu) => qu.role_type_id === ROLES['editor']['value'],
                        ).length > 0 ? (
                          questionUsers
                            .filter(
                              (qu) =>
                                qu.role_type_id === ROLES['editor']['value'],
                            )
                            .map((qu, index) => (
                              <h6 key={qu.id}>
                                {index === 0
                                  ? `${qu.first_name} ${qu.last_name}`
                                  : `, ${qu.first_name} ${qu.last_name}`}
                              </h6>
                            ))
                        ) : (
                          <span>
                            {t('no')} {t('editors')}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-roles modal-custom-body p20 mt-20 mb-20">
              <div className="role-section">
                <h6 className="header">{t('story_prompt_role')}</h6>
                <div className="roles">
                  {isGettingUsers ? (
                    <StyledLoader isglobal={false} />
                  ) : (
                    <>
                      {questionUsers.filter(
                        (qu) =>
                          qu.role_type_id !== ROLES['purchaser']['value'] &&
                          qu.role_type_id !== ROLES['editor']['value'] &&
                          qu.role_type_id !== ROLES['guest']['value'],
                      ).length > 0 ? (
                        questionUsers
                          .filter(
                            (qu) =>
                              qu.role_type_id !== ROLES['purchaser']['value'] &&
                              qu.role_type_id !== ROLES['editor']['value'] &&
                              qu.role_type_id !== ROLES['guest']['value'],
                          )
                          .map((qu) => (
                            <div className="user-role" key={qu.id}>
                              <h6 className="user-name">{`${qu.first_name} ${qu.last_name}`}</h6>
                              <Radio.Group
                                onChange={(e) =>
                                  onChangeUserRoleOption(qu.id, e.target.value)
                                }
                                value={userRoleOptions[qu.id]}
                                disabled={
                                  type === 3 &&
                                  userCurrentRoles[qu.id] === 'author'
                                }
                              >
                                <Space direction="horizontal" size={16}>
                                  {rolesToSet.map((roleId) => (
                                    <Radio key={roleId} value={roleId}>
                                      {roleId === 'author' && type === 3
                                        ? `${ROLES[roleId][`label_${lang}`]}*`
                                        : ROLES[roleId][`label_${lang}`]}
                                    </Radio>
                                  ))}
                                </Space>
                              </Radio.Group>
                            </div>
                          ))
                      ) : (
                        <></>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {type === 3 && (
          <div className="modal-roles modal-custom-body p20">
            <div className="narration">
              <span>
                {t(
                  type === 3
                    ? 'modal_send_prompt_narration_1'
                    : 'modal_send_prompt_narration_2',
                )}
              </span>
            </div>
          </div>
        )}

        <div className="modal-roles modal-custom-footer">
          <div className="actions">
            {type !== 1 && (
              <Button className="btn-white btn-dash" onClick={handleCloseModal}>
                {t('cancel')}
              </Button>
            )}

            <Button
              className={`${type === 1 ? 'btn-white' : 'btn-blue'} btn-dash`}
              onClick={actions[type]}
            >
              {t(actionButtonTitles[type])}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        title={t('tt_email_prompt')}
        open={type === 4}
        onCancel={handleCloseModal}
        maskClosable={false}
        closable={true}
        footer={false}
        wrapClassName="modal-roles-wrapper"
        getContainer={() => document.getElementById('story-section')}
      >
        <h3>
          <i>{question.dsply_text}</i>
        </h3>
        <br />
        <h4>{t('confirm_to_proceed')}</h4>
        <div className="modal-roles modal-custom-footer">
          <div className="actions">
            <Button className="btn-redLight gray" onClick={handleCloseModal}>
              {t('cancel')}
            </Button>

            <Button className="btn-darkgreen blue" onClick={sendPrompt}>
              Send now
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}
